/* Lógica pura de la pestaña Comisiones: estado editable desde el GET, valores efectivos con herencia,
   validación y cuerpo del POST. Los campos numéricos viven como texto (así se editan en un input);
   "" en un campo del profesional significa "hereda" (null en la API). */

const aTexto = (n) => (n === null || n === undefined || n === "" ? "" : String(n));

export const aNumero = (v) => {
  if (v === null || v === undefined) return NaN;
  const t = String(v).trim().replace(",", ".");
  return t === "" ? NaN : Number(t);
};

/** GET body -> estado editable. Filas en el orden del catálogo, columnas en el de `professionals`. */
export function estadoDeComisiones(data) {
  const settings = data?.settings || {};
  return {
    enabled: settings.enabled !== false,
    onlyFullyPaid: settings.onlyFullyPaid !== false,
    serviceDefaultPercent: aTexto(settings.serviceDefaultPercent),
    productDefaultPercent: aTexto(settings.productDefaultPercent),
    services: (data?.services || []).map((s) => ({ id: String(s.id), name: s.name || "" })),
    professionals: (data?.professionals || []).map((p) => ({
      id: String(p.id),
      name: p.name || "",
      serviceDefaultPercent: aTexto(p.serviceDefaultPercent),
      productPercent: aTexto(p.productPercent),
      // serviceId -> override | null. Solo están los servicios que el profesional hace.
      services: Object.fromEntries(
        (p.services || []).map((s) => [
          String(s.serviceId),
          s.override ? { type: s.override.type === "fixed" ? "fixed" : "percent", value: aTexto(s.override.value) } : null,
        ])
      ),
    })),
  };
}

export const hace = (profesional, serviceId) => Object.prototype.hasOwnProperty.call(profesional.services, serviceId);

const miles = (n) => {
  const [ent, dec] = String(n).split(".");
  return ent.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + (dec ? `,${dec}` : "");
};

/** { type, value } -> "45 %" o "$ 6.000 fijo". */
export function formatoComision({ type, value }) {
  return type === "fixed" ? `$ ${miles(value)} fijo` : `${miles(value)} %`;
}

/** Porcentaje por defecto que aplica a un profesional: el suyo, si no el del salón (NaN si falta ambos). */
export function porcentajeBaseDelProfesional(state, profesional) {
  const propio = aNumero(profesional.serviceDefaultPercent);
  return Number.isNaN(propio) ? aNumero(state.serviceDefaultPercent) : propio;
}

/** Celda profesional x servicio: { hace:false } o { hace:true, propia, type, value }. */
export function valorDeCelda(state, profesional, serviceId) {
  if (!hace(profesional, serviceId)) return { hace: false };
  const override = profesional.services[serviceId];
  const valor = override ? aNumero(override.value) : NaN;
  if (override && !Number.isNaN(valor)) return { hace: true, propia: true, type: override.type, value: valor };
  return { hace: true, propia: false, type: "percent", value: porcentajeBaseDelProfesional(state, profesional) };
}

/** Producto de un profesional: el suyo, si no el del salón. */
export function valorDeProducto(state, profesional) {
  const propio = aNumero(profesional.productPercent);
  if (!Number.isNaN(propio)) return { propia: true, value: propio };
  return { propia: false, value: aNumero(state.productDefaultPercent) };
}

/** Texto de una celda calculada; "" si no hay valor para mostrar. */
export function textoDeValor(v) {
  if (v.hace === false || Number.isNaN(v.value)) return "";
  return formatoComision(v);
}

const errorPorcentaje = (v, etiqueta, { obligatorio }) => {
  if (v === "" || v === null || v === undefined) return obligatorio ? `Completá ${etiqueta}.` : null;
  const n = aNumero(v);
  if (Number.isNaN(n)) return `${etiqueta}: ingresá un número.`;
  if (n < 0 || n > 100) return `${etiqueta}: el porcentaje va de 0 a 100.`;
  return null;
};

/** Primer error de validación (en español) o null. */
export function faltaComisiones(state) {
  const salonServicios = errorPorcentaje(state.serviceDefaultPercent, "el porcentaje por defecto del salón en servicios", { obligatorio: true });
  if (salonServicios) return salonServicios;
  const salonProductos = errorPorcentaje(state.productDefaultPercent, "el porcentaje por defecto del salón en productos", { obligatorio: true });
  if (salonProductos) return salonProductos;
  const nombreDe = (id) => state.services.find((s) => s.id === id)?.name || "servicio";
  for (const p of state.professionals) {
    const base = errorPorcentaje(p.serviceDefaultPercent, `el porcentaje por defecto de ${p.name}`, { obligatorio: false });
    if (base) return base;
    const prod = errorPorcentaje(p.productPercent, `el porcentaje de productos de ${p.name}`, { obligatorio: false });
    if (prod) return prod;
    for (const [serviceId, o] of Object.entries(p.services)) {
      if (!o) continue;
      const etiqueta = `${p.name} en ${nombreDe(serviceId)}`;
      if (o.type === "fixed") {
        const n = aNumero(o.value);
        if (Number.isNaN(n)) return `${etiqueta}: ingresá el monto fijo.`;
        if (n < 0) return `${etiqueta}: el monto fijo no puede ser negativo.`;
      } else {
        const e = errorPorcentaje(o.value, etiqueta, { obligatorio: true });
        if (e) return e;
      }
    }
  }
  return null;
}

/** Estado -> cuerpo del POST (mismo formato que el GET, sin nombres ni catálogo). */
export function payloadComisiones(state) {
  const opcional = (v) => {
    const n = aNumero(v);
    return Number.isNaN(n) ? null : n;
  };
  return {
    settings: {
      enabled: state.enabled !== false,
      onlyFullyPaid: !!state.onlyFullyPaid,
      serviceDefaultPercent: aNumero(state.serviceDefaultPercent),
      productDefaultPercent: aNumero(state.productDefaultPercent),
    },
    professionals: state.professionals.map((p) => ({
      id: p.id,
      serviceDefaultPercent: opcional(p.serviceDefaultPercent),
      productPercent: opcional(p.productPercent),
      services: Object.entries(p.services).map(([serviceId, o]) => ({
        serviceId,
        override: o ? { type: o.type, value: aNumero(o.value) } : null,
      })),
    })),
  };
}
